import {
  Alert,
  Anchor,
  Button,
  Card,
  Checkbox,
  Group,
  List,
  Radio,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import { Link } from "react-router";
import { z } from "zod";
import { isAuthorizationError, useAssurance } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
  StatusBadge,
} from "@how/ui";
import { RecordList } from "../domains/DnsRecords";
import { checkedAt, DOMAIN_STATUS_LABELS } from "../website/domain-queries";
import {
  churchDomainProblem,
  freeSurfaces,
  hasLiveAddress,
  isSettling,
  pausedLines,
  SIGN_IN_SURFACES,
  signInErrorMessage,
  signInHost,
  tidyChurchDomain,
  useRecheckSignInAddress,
  useRemoveSignInAddress,
  useRequestSignInAddress,
  useSetPreferredAddress,
  useSignInSetup,
  WHO,
  type SignInAddress,
  type SignInSetup,
  type SignInSurface,
} from "./sign-in-addresses-queries";

const LEAD = "Let staff and members sign in on your church's own domain.";

/** The spec's header (section 10), naming HoW's addresses once they are known. */
function headerDescription(setup: SignInSetup | undefined): string {
  if (setup === undefined) return LEAD;
  return `${LEAD} HoW's addresses keep working: ${setup.platformAddresses.staff} and ${setup.platformAddresses.members}.`;
}

/** What is happening now, in a sentence, under the badge (a live address has its own line). */
function progressLine(address: SignInAddress): string {
  switch (address.status) {
    case "pending_verification":
      return "HoW is looking for your DNS record. Usually a few minutes after you add it; some DNS companies take up to a day. This page updates on its own.";
    case "verifying":
      return "Your record is right; HoW is getting the certificate.";
    case "active":
      return "Live.";
    case "failed":
      return "HoW could not connect this address. Check the record below, then choose Check now.";
    case "disabled":
      return "DCX Logic turned this address off. Contact support@dcxlogic.com.";
  }
}

function AddressCard({
  address,
  platform,
  canChange,
}: {
  readonly address: SignInAddress;
  readonly platform: SignInSetup["platformAddresses"];
  readonly canChange: boolean;
}) {
  const recheck = useRecheckSignInAddress();
  const remove = useRemoveSignInAddress();
  const [confirming, setConfirming] = useState(false);
  const routing = address.records.filter((r) => r.purpose === "routing");
  const extra = address.records.filter((r) => r.purpose !== "routing");
  const live = address.status === "active";
  const who = WHO[address.surface];
  const home = address.surface === "staff_dashboard" ? platform.staff : platform.members;

  return (
    <Card data-sign-in-address={address.hostname}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" gap="sm">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text size="xs" c="dimmed">
              {address.surface === "staff_dashboard" ? "Staff sign-in" : "Member sign-in"}
            </Text>
            <Title order={3} size="h5" style={{ overflowWrap: "anywhere" }}>
              {address.hostname}
            </Title>
            <Group gap="xs">
              <StatusBadge
                domain="domain_status"
                value={address.status}
                label={DOMAIN_STATUS_LABELS[address.status]}
              />
              {address.lastCheckedAt !== null && !live ? (
                <Text size="xs" c="dimmed">
                  Last checked {checkedAt(address.lastCheckedAt)}
                </Text>
              ) : null}
            </Group>
          </Stack>
          {canChange ? (
            <Group gap="xs">
              {live || address.status === "disabled" ? null : (
                <Button
                  variant="default"
                  size="compact-sm"
                  style={{ flexShrink: 0 }}
                  loading={recheck.isPending}
                  aria-label={`Check now: ${address.hostname}`}
                  onClick={() => {
                    recheck.mutate(address.id, {
                      onSuccess: () => {
                        notifications.show({
                          color: semanticColor("info"),
                          title: "Checking now",
                          message: `HoW is looking at ${address.hostname} again. This page updates on its own.`,
                        });
                      },
                      onError: (error) => {
                        notifications.show({
                          color: semanticColor("danger"),
                          title: "Could not check",
                          message: signInErrorMessage(error),
                        });
                      },
                    });
                  }}
                >
                  Check now
                </Button>
              )}
              <Button
                variant="subtle"
                color={semanticColor("danger")}
                size="compact-sm"
                style={{ flexShrink: 0 }}
                aria-label={`Remove ${address.hostname}`}
                onClick={() => {
                  setConfirming(true);
                }}
              >
                Remove
              </Button>
            </Group>
          ) : null}
        </Group>

        {live ? (
          <Text size="sm" role="status" style={{ overflowWrap: "anywhere" }}>
            {who} can sign in at{" "}
            <Anchor
              href={`https://${address.hostname}`}
              target="_blank"
              rel="noreferrer"
              // The visible address first, then where it opens (SC 2.5.3, UI_STANDARDS).
              aria-label={`https://${address.hostname} (opens in a new tab)`}
            >
              {`https://${address.hostname}`}
            </Anchor>
            . {home} keeps working too.
          </Text>
        ) : (
          <Text size="sm" role="status">
            {progressLine(address)}
          </Text>
        )}
        {address.lastError !== null && !live ? (
          <Alert
            color={semanticColor(address.status === "failed" ? "danger" : "info")}
            title={address.status === "failed" ? "What went wrong" : "What HoW last saw"}
            style={{ overflowWrap: "anywhere" }}
          >
            {address.lastError}
          </Alert>
        ) : null}

        {live || address.status === "disabled" ? null : (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              1. Add this record for {address.domain}
            </Text>
            <Text size="sm" c="dimmed">
              At the company that runs your domain's DNS, add this record. If your domain is on
              Cloudflare, set it to DNS only (grey cloud).
            </Text>
            <RecordList records={routing} caption={`The record to add for ${address.hostname}`} />
            {extra.length > 0 ? (
              <>
                <Text size="sm" c="dimmed">
                  Optional. Add this record too if you want the certificate issued before the
                  address above points to HoW.
                </Text>
                <RecordList records={extra} caption={`Optional record for ${address.hostname}`} />
              </>
            ) : null}
            <Text size="sm" fw={600}>
              2. Wait for Live
            </Text>
            <Text size="sm" c="dimmed">
              You can close this page. HoW checks for you, usually within a few minutes, and gets
              the security certificate.
            </Text>
          </Stack>
        )}
      </Stack>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={`Remove ${address.hostname}?`}
        message={`${who} signed in there will need to sign in at ${home}. If it is your last address, your preferred address goes back to HoW's. Also delete the CNAME record at your DNS company.`}
        confirmLabel="Remove"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(address.id, {
            onSuccess: () => {
              setConfirming(false);
              notifications.show({
                color: semanticColor("success"),
                title: "Address removed",
                message: `${address.hostname} is no longer connected. ${home} keeps working.`,
              });
            },
            onError: (error) => {
              setConfirming(false);
              notifications.show({
                color: semanticColor("danger"),
                title: "Address not removed",
                message: signInErrorMessage(error),
              });
            },
          });
        }}
      />
    </Card>
  );
}

const addSchema = z.object({
  domain: z.string().superRefine((value, ctx) => {
    const problem = churchDomainProblem(value);
    if (problem !== null) ctx.addIssue({ code: "custom", message: problem });
  }),
  surfaces: z
    .array(z.enum(["staff_dashboard", "member_portal"]))
    .min(1, "Choose staff, members or both."),
});
type AddValues = z.infer<typeof addSchema>;

/** The owner types the church's domain; HoW proposes dash. and members. (ADR 0067 R5, S5). */
function AddForm({
  free,
  suggested,
}: {
  readonly free: readonly SignInSurface[];
  readonly suggested: string | null;
}) {
  const request = useRequestSignInAddress();
  const form = useForm<AddValues>({
    initialValues: { domain: suggested ?? "", surfaces: [...free] },
    validate: zod4Resolver(addSchema),
  });
  const typed = tidyChurchDomain(form.values.domain);
  const shown = churchDomainProblem(typed) === null ? typed : "yourchurch.org";

  const submit = async (values: AddValues): Promise<void> => {
    const domain = tidyChurchDomain(values.domain);
    const chosen = SIGN_IN_SURFACES.filter((s) => free.includes(s) && values.surfaces.includes(s));
    if (chosen.length === 0) {
      form.setFieldError("surfaces", "Choose staff, members or both.");
      return;
    }
    const added: string[] = [];
    for (const surface of chosen) {
      try {
        await request.mutateAsync({ surface, domain });
        added.push(signInHost(surface, domain));
      } catch (error) {
        form.setFieldError("domain", signInErrorMessage(error));
        break;
      }
    }
    if (added.length > 0) {
      notifications.show({
        color: semanticColor("success"),
        title: added.length === 1 ? "Address added" : "Addresses added",
        message: `Now add the record shown for ${added.join(" and ")}.`,
      });
    }
    if (added.length === chosen.length) form.setFieldValue("surfaces", []);
  };

  return (
    <Card component="section" aria-labelledby="sign-in-add">
      <form
        onSubmit={form.onSubmit((values) => {
          void submit(values);
        })}
      >
        <Stack gap="sm">
          <div>
            <Title order={2} size="h4" id="sign-in-add">
              Add a sign-in address
            </Title>
            <Text size="sm" c="dimmed">
              Enter your church's domain. HoW proposes dash. for staff and members. for members, and
              shows the one record to add for each. It is part of every plan.
            </Text>
          </div>
          <TextInput
            label="Your church's domain"
            description="Without www, such as yourchurch.org."
            placeholder="yourchurch.org"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="url"
            maw={480}
            {...form.getInputProps("domain")}
          />
          <Checkbox.Group label="Who signs in there" {...form.getInputProps("surfaces")}>
            <Stack gap="xs" mt="xs">
              {free.map((s) => (
                <Checkbox key={s} value={s} label={`${WHO[s]}: ${signInHost(s, shown)}`} />
              ))}
            </Stack>
          </Checkbox.Group>
          <Group>
            <Button type="submit" loading={request.isPending}>
              Add
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

/** "Our preferred address" (spec section 9, ADR 0067 R9, S6): where HoW's own links point; it moves nobody. */
function PreferredCard({
  setup,
  canChange,
}: {
  readonly setup: SignInSetup;
  readonly canChange: boolean;
}) {
  const save = useSetPreferredAddress();
  const live = hasLiveAddress(setup.addresses);
  const paused = pausedLines(setup);
  return (
    <Card component="section" aria-labelledby="sign-in-preferred">
      <Stack gap="sm">
        <div>
          <Title order={2} size="h4" id="sign-in-preferred">
            Our preferred address
          </Title>
          <Text size="sm" c="dimmed">
            Where HoW's own links take your staff and members. Both addresses always work; nobody is
            moved from the address they opened. Moving to another address asks them to confirm their
            account, and staff enter their two-step code there once.
          </Text>
        </div>
        <Radio.Group
          label="Where HoW's own links point"
          value={setup.preferred}
          onChange={(value) => {
            if (save.isPending || (value !== "how" && value !== "church")) return;
            save.mutate(value, {
              onSuccess: () => {
                notifications.show({
                  color: semanticColor("success"),
                  title: "Preferred address saved",
                  message:
                    value === "church"
                      ? "HoW's own links now use your church's address wherever it is live."
                      : "HoW's own links now use HoW's address.",
                });
              },
              onError: (error) => {
                notifications.show({
                  color: semanticColor("danger"),
                  title: "Not saved",
                  message: signInErrorMessage(error),
                });
              },
            });
          }}
        >
          <Stack gap="xs" mt="xs">
            <Radio
              value="how"
              label="HoW's address"
              description={`${setup.platformAddresses.staff} and ${setup.platformAddresses.members}`}
              disabled={!canChange}
            />
            <Radio
              value="church"
              label="Our own address"
              description={
                live
                  ? "Your live sign-in addresses, for each app that has one."
                  : "Available once one of your addresses is Live"
              }
              disabled={!canChange || (!live && setup.preferred !== "church")}
            />
          </Stack>
        </Radio.Group>
        {paused.map((line) => (
          <Alert key={line} color={semanticColor("warning")}>
            {line}
          </Alert>
        ))}
      </Stack>
    </Card>
  );
}

function HowItWorks() {
  return (
    <Card component="section" aria-labelledby="sign-in-how">
      <Stack gap="xs">
        <Title order={2} size="h4" id="sign-in-how">
          How it works
        </Title>
        <List type="ordered" size="sm" spacing={6}>
          <List.Item>
            Enter your church's domain, such as yourchurch.org, and choose staff, members or both.
          </List.Item>
          <List.Item>
            Add the one record this page shows for each address, at the company that runs your
            domain's DNS.
          </List.Item>
          <List.Item>
            Wait for Live. HoW checks for you, usually within a few minutes, and gets the security
            certificate.
          </List.Item>
          <List.Item>
            Choose Our preferred address if you want HoW's own links to use your address. HoW's
            addresses always keep working, and bookmarks keep working.
          </List.Item>
        </List>
      </Stack>
    </Card>
  );
}

/**
 * Settings → Sign-in addresses (ADR 0067 phase B, piece 3): the church's own addresses for the staff dashboard and the
 * member portal, beside HoW's, which always work. Owner only (domains.manage_signin, the route); every change needs
 * two-step (S2) and is refused in a support session (S3). The database enforces all of it.
 */
export function SignInAddressesPage() {
  const setup = useSignInSetup();
  const assurance = useAssurance();
  const header = (
    <PageHeader title="Sign-in addresses" description={headerDescription(setup.data)} />
  );
  if (setup.isPending || assurance.isPending) {
    return (
      <>
        {header}
        <LoadingState rows={3} label="Loading your sign-in addresses" />
      </>
    );
  }
  if (setup.isError) {
    return (
      <>
        {header}
        {isAuthorizationError(setup.error) ? (
          <PermissionDenied />
        ) : (
          <ErrorState
            message="Could not load your sign-in addresses."
            onRetry={() => {
              void setup.refetch();
            }}
          />
        )}
      </>
    );
  }
  const data = setup.data;
  // Fails closed: a level that could not be read counts as no two-step (S2).
  const canChange = assurance.data?.currentLevel === "aal2";
  // A known aal1 is told to set up two-step; a level that could not be read is told to reload.
  const levelUnknown = assurance.data === undefined || assurance.isError;
  const free = freeSurfaces(data.addresses);
  const settling = data.addresses.some(isSettling);
  return (
    <>
      {header}
      <Stack gap="md">
        {canChange ? null : levelUnknown ? (
          <Alert color={semanticColor("info")} title="Two-step status unknown">
            <Text size="sm">
              We couldn't check your two-step status. Reload the page, or{" "}
              <Anchor component={Link} to="/security">
                open Security
              </Anchor>
              .
            </Text>
          </Alert>
        ) : (
          <Alert
            color={semanticColor("info")}
            title="Set up two-step verification to change sign-in addresses"
          >
            <Text size="sm">
              These addresses decide where your staff type their passwords, so HoW asks for your
              two-step code for every change.{" "}
              <Anchor component={Link} to="/security">
                Open Security
              </Anchor>{" "}
              to set it up, then come back.
            </Text>
          </Alert>
        )}
        <Stack gap="sm" component="section" aria-labelledby="sign-in-yours">
          <Title order={2} size="h4" id="sign-in-yours">
            Your addresses
          </Title>
          {data.addresses.length === 0 ? (
            <EmptyState
              title="No sign-in addresses yet"
              description={`Staff sign in at ${data.platformAddresses.staff} and members at ${data.platformAddresses.members}.`}
            />
          ) : (
            data.addresses.map((a) => (
              <AddressCard
                key={a.id}
                address={a}
                platform={data.platformAddresses}
                canChange={canChange}
              />
            ))
          )}
          {/* Announced once for the whole page, so a screen reader hears that it refreshes by itself. */}
          {settling ? (
            <Text size="xs" c="dimmed" aria-live="polite">
              This page refreshes about every 15 seconds while an address is on its way to Live.
            </Text>
          ) : null}
        </Stack>
        {canChange && free.length > 0 ? (
          <AddForm free={free} suggested={data.websiteDomain} />
        ) : null}
        <PreferredCard setup={data} canChange={canChange} />
        <HowItWorks />
      </Stack>
    </>
  );
}
